/**
 * EVENTSFLO-CLIENT-PORTAL-PHASE-1.5-QUOTES-INVOICES-V1
 * Client Invoices — read-only list + detail modal with PDF download.
 *
 * Strictly scoped to the authenticated client via the JWT.
 * Draft invoices are never shown.
 */
import { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Separator } from "../../components/ui/separator";
import { ScrollArea } from "../../components/ui/scroll-area";
import { toast } from "sonner";
import {
  Receipt,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Filter,
  Download,
  Eye,
  Calendar,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const VISIBLE_INVOICE_STATUSES = [
  "issued",
  "sent",
  "partial",
  "paid",
  "overdue",
  "cancelled",
];

const statusColor = (status) => {
  switch ((status || "").toLowerCase()) {
    case "paid":
      return "bg-green-500/10 text-green-600 border-green-500/20";
    case "issued":
    case "sent":
      return "bg-blue-500/10 text-blue-600 border-blue-500/20";
    case "partial":
      return "bg-amber-500/10 text-amber-600 border-amber-500/20";
    case "overdue":
      return "bg-red-500/10 text-red-600 border-red-500/20";
    case "cancelled":
      return "bg-muted text-muted-foreground border-border";
    default:
      return "bg-muted text-muted-foreground";
  }
};

const formatMoney = (amount, currencySymbol) => {
  const num = Number(amount || 0);
  const sym = currencySymbol || "€";
  return `${sym}${num.toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

export default function ClientInvoicesPage() {
  const { getToken } = useOutletContext();
  // EVENTSFLO-PORTAL-I18N-PHASE-2B-CLIENT-PORTAL-COVERAGE-V1
  const { t } = useTranslation(["clientPortal", "common"]);

  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [meta, setMeta] = useState({
    page: 1,
    limit: 20,
    total: 0,
    total_pages: 0,
  });
  const [statusFilter, setStatusFilter] = useState("all");
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchInvoices = useCallback(
    async (page = 1, status = "all") => {
      const token = getToken();
      if (!token) return;

      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: page.toString(),
          page_size: "20",
        });
        if (status && status !== "all") {
          params.append("status", status);
        }

        const response = await fetch(
          `${API_URL}/api/client/invoices?${params}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response.ok) throw new Error("Failed to fetch invoices");
        const data = await response.json();
        setInvoices(data.data || []);
        setMeta(
          data.meta || { page: 1, limit: 20, total: 0, total_pages: 0 }
        );
      } catch (err) {
        console.error("Invoices fetch error:", err);
        toast.error(t("clientPortal:invoices.fetchFailed"));
      } finally {
        setLoading(false);
      }
    },
    [getToken, t]
  );

  useEffect(() => {
    fetchInvoices(1, statusFilter);
  }, [fetchInvoices, statusFilter]);

  const openDetail = async (invoiceId) => {
    const token = getToken();
    if (!token) return;
    setDetail(null);
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/client/invoices/${invoiceId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error("Failed to fetch invoice");
      const data = await response.json();
      setDetail(data);
    } catch (err) {
      console.error("Invoice detail error:", err);
      toast.error(t("clientPortal:invoices.detailFailed"));
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const viewPdf = async (invoiceId) => {
    const token = getToken();
    if (!token) return;
    try {
      const response = await fetch(
        `${API_URL}/api/client/invoices/${invoiceId}/pdf`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error("Failed to load PDF");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const win = window.open(url, "_blank", "noopener,noreferrer");
      if (!win) {
        toast.error(t("clientPortal:invoices.popupBlocked"));
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      console.error("PDF view error:", err);
      toast.error(t("clientPortal:invoices.pdfViewFailed"));
    }
  };

  const downloadPdf = async (invoiceId, invoiceNumber) => {
    const token = getToken();
    if (!token) return;
    try {
      const response = await fetch(
        `${API_URL}/api/client/invoices/${invoiceId}/pdf`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error("Failed to download PDF");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Invoice_${invoiceNumber || invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t("clientPortal:invoices.pdfDownloaded"));
    } catch (err) {
      console.error("PDF download error:", err);
      toast.error(t("clientPortal:invoices.pdfDownloadFailed"));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold" data-testid="invoices-title">
            {t("clientPortal:invoices.title")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("clientPortal:invoices.totalLabel", { count: meta.total })}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger
              className="w-[160px]"
              data-testid="invoice-status-filter"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("clientPortal:invoices.filterAllStatuses")}</SelectItem>
              {VISIBLE_INVOICE_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {t(`clientPortal:invoices.status.${s}`, {
                    defaultValue: s.charAt(0).toUpperCase() + s.slice(1),
                  })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : invoices.length === 0 ? (
        <Card>
          <CardContent
            className="py-12 text-center"
            data-testid="invoices-empty-state"
          >
            <Receipt className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">{t("clientPortal:invoices.empty")}</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-2">
            {invoices.map((inv) => (
              <Card
                key={inv.id}
                className="hover:bg-accent/30 transition-colors"
                data-testid={`invoice-row-${inv.id}`}
              >
                <CardContent className="py-4">
                  <div className="flex items-start gap-4">
                    <div className="shrink-0 w-12 h-12 bg-primary/10 text-primary rounded-lg flex items-center justify-center">
                      <Receipt className="h-5 w-5" />
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <h3 className="font-semibold text-sm">
                          {inv.invoice_number || t("clientPortal:invoices.pendingNumber")}
                        </h3>
                        <Badge
                          variant="outline"
                          className={`text-[10px] capitalize ${statusColor(inv.status)}`}
                        >
                          {t(`clientPortal:invoices.status.${(inv.status || "").toLowerCase()}`, {
                            defaultValue: inv.status,
                          })}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {inv.project_name || "—"}
                      </p>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {t("clientPortal:invoices.issuedPrefix", { date: formatDate(inv.issue_date) })}
                        </span>
                        <span>{t("clientPortal:invoices.duePrefix", { date: formatDate(inv.due_date) })}</span>
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <div className="text-right">
                        <div
                          className="text-base font-semibold"
                          data-testid={`invoice-total-${inv.id}`}
                        >
                          {formatMoney(inv.total, inv.currency_symbol)}
                        </div>
                        {Number(inv.amount_paid) > 0 && (
                          <div className="text-[10px] text-muted-foreground">
                            {t("clientPortal:invoices.paidLabel", { amount: formatMoney(inv.amount_paid, inv.currency_symbol) })}
                          </div>
                        )}
                        {Number(inv.balance_due) > 0 &&
                          Number(inv.amount_paid) > 0 && (
                            <div className="text-[10px] text-amber-600">
                              {t("clientPortal:invoices.balanceDueLabel", { amount: formatMoney(inv.balance_due, inv.currency_symbol) })}
                            </div>
                          )}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDetail(inv.id)}
                          data-testid={`invoice-view-${inv.id}`}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          {t("clientPortal:invoices.actions.detail")}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => viewPdf(inv.id)}
                          data-testid={`invoice-view-pdf-${inv.id}`}
                          title={t("clientPortal:invoices.actions.viewPdf")}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          {t("clientPortal:invoices.actions.viewPdf")}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadPdf(inv.id, inv.invoice_number)}
                          data-testid={`invoice-download-${inv.id}`}
                          title={t("clientPortal:invoices.actions.viewPdf")}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          {t("clientPortal:invoices.actions.download")}
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {meta.total_pages > 1 && (
            <div className="flex items-center justify-between pt-4">
              <p className="text-sm text-muted-foreground">
                {t("clientPortal:invoices.pagination.pageOf", { page: meta.page, total: meta.total_pages })}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={meta.page <= 1}
                  onClick={() => fetchInvoices(meta.page - 1, statusFilter)}
                  data-testid="invoices-prev-page-btn"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  {t("clientPortal:invoices.pagination.previous")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={meta.page >= meta.total_pages}
                  onClick={() => fetchInvoices(meta.page + 1, statusFilter)}
                  data-testid="invoices-next-page-btn"
                >
                  {t("clientPortal:invoices.pagination.next")}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Detail Modal */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent
          className="max-w-2xl max-h-[85vh]"
          data-testid="invoice-detail-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-primary" />
              {detail?.invoice_number || t("clientPortal:invoices.detail.fallbackTitle")}
              {detail?.status && (
                <Badge
                  variant="outline"
                  className={`text-[10px] capitalize ml-2 ${statusColor(detail.status)}`}
                >
                  {t(`clientPortal:invoices.status.${(detail.status || "").toLowerCase()}`, {
                    defaultValue: detail.status,
                  })}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          {detailLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : detail ? (
            <ScrollArea className="max-h-[60vh] pr-4">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground">{t("clientPortal:invoices.detail.project")}</p>
                    <p className="font-medium">{detail.project_name || "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t("clientPortal:invoices.detail.issueDate")}</p>
                    <p className="font-medium">
                      {formatDate(detail.issue_date)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t("clientPortal:invoices.detail.dueDate")}</p>
                    <p className="font-medium">
                      {formatDate(detail.due_date)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t("clientPortal:invoices.detail.currency")}</p>
                    <p className="font-medium">
                      {detail.currency_code || "EUR"}
                    </p>
                  </div>
                </div>

                <Separator />

                <div>
                  <h4 className="text-sm font-semibold mb-2">{t("clientPortal:invoices.detail.lineItems")}</h4>
                  <div className="space-y-1 text-xs">
                    {(detail.lines || []).map((ln, i) => (
                      <div
                        key={i}
                        className="grid grid-cols-12 gap-2 py-1.5 border-b border-border/40"
                        data-testid={`invoice-line-${i}`}
                      >
                        <div className="col-span-7">
                          <p className="font-medium">{ln.description}</p>
                        </div>
                        <div className="col-span-2 text-right">
                          {ln.quantity}
                        </div>
                        <div className="col-span-3 text-right">
                          {formatMoney(ln.line_total, detail.currency_symbol)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <Separator />

                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t("clientPortal:invoices.detail.subtotal")}</span>
                    <span>
                      {formatMoney(detail.subtotal, detail.currency_symbol)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t("clientPortal:invoices.detail.vat")}</span>
                    <span>
                      {formatMoney(detail.tax_amount, detail.currency_symbol)}
                    </span>
                  </div>
                  <div className="flex justify-between font-semibold text-base pt-1">
                    <span>{t("clientPortal:invoices.detail.total")}</span>
                    <span data-testid="invoice-detail-total">
                      {formatMoney(detail.total, detail.currency_symbol)}
                    </span>
                  </div>
                  {Number(detail.amount_paid) > 0 && (
                    <>
                      <div className="flex justify-between text-green-600">
                        <span>{t("clientPortal:invoices.detail.amountPaid")}</span>
                        <span>
                          {formatMoney(
                            detail.amount_paid,
                            detail.currency_symbol
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between font-medium">
                        <span>{t("clientPortal:invoices.detail.balanceDue")}</span>
                        <span data-testid="invoice-detail-balance">
                          {formatMoney(
                            detail.balance_due,
                            detail.currency_symbol
                          )}
                        </span>
                      </div>
                    </>
                  )}
                </div>

                {detail.notes && (
                  <>
                    <Separator />
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">
                        {t("clientPortal:invoices.detail.notes")}
                      </p>
                      <p className="text-sm whitespace-pre-wrap">
                        {detail.notes}
                      </p>
                    </div>
                  </>
                )}

                <div className="pt-3 flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => viewPdf(detail.id)}
                    data-testid="invoice-detail-view-pdf-btn"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    {t("clientPortal:invoices.detail.viewPdf")}
                  </Button>
                  <Button
                    onClick={() =>
                      downloadPdf(detail.id, detail.invoice_number)
                    }
                    data-testid="invoice-detail-download-btn"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    {t("clientPortal:invoices.detail.downloadPdf")}
                  </Button>
                </div>
              </div>
            </ScrollArea>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

